import { LiveData } from '@toeverything/infra';

import { assessBrowserStoragePressure } from './browser-storage-pressure';

/** How often a full device is checked for free space again. */
export const DEVICE_STORAGE_RECHECK_MS = 60_000;

/**
 * Whether this device has no room left for the workspace: the browser's quota
 * is nearly used, or a write was refused for lack of space. While full, the
 * quota is read again every minute; the device counts as full until the quota
 * shows room again.
 */
export class DeviceStorageFull {
  readonly full$ = new LiveData(false);
  private pressured = false;
  private refused = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly estimate: (() => Promise<StorageEstimate>) | null
  ) {}

  /** The newest estimate of the browser's quota. */
  pressure(pressured: boolean) {
    this.pressured = pressured;
    // Room again: a write refused before can succeed now.
    if (!pressured) this.refused = false;
    this.update();
  }

  /** A write was refused for lack of space. */
  refusedWrite() {
    this.refused = true;
    this.update();
  }

  dispose() {
    this.stopRechecks();
  }

  private update() {
    const full = this.pressured || this.refused;
    if (this.full$.value !== full) this.full$.next(full);
    if (!full) {
      this.stopRechecks();
      return;
    }
    if (this.timer || !this.estimate) return;
    const estimate = this.estimate;
    this.timer = setInterval(() => {
      estimate().then(
        next => {
          const pressure = assessBrowserStoragePressure(next);
          if (pressure) this.pressure(pressure.pressured);
        },
        () => {}
      );
    }, DEVICE_STORAGE_RECHECK_MS);
  }

  private stopRechecks() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
